/**
 * @file appointment.schema.ts
 * @description Consulta agendada pelo app ou vinda do Calendly
 * (coleção 'appointments').
 *
 * Não confundir com `consultation.schema.ts`, que é registro retrospectivo de
 * prontuário. Aqui é o booking: o que a usuária marcou, quando, com quem.
 *
 * Todos os instantes são gravados em UTC. `timezone` viaja separado e é o que
 * define como formatar para exibição — nunca derive o fuso do offset da data.
 */

import { z } from 'zod';

import { CalendlyLocationKindEnum, PreparationCardSchema } from './scheduling-common.schema';

/**
 * PENDING   — doc criado ANTES do POST /invitees, para que o id possa viajar em
 *             `tracking.utm_content` e voltar identificado no webhook.
 * SCHEDULED — invitee criado no Calendly com sucesso.
 * CANCELED  — cancelado pela usuária, pelo profissional ou pela Eva.
 * COMPLETED / NO_SHOW — desfecho, preenchido depois da consulta.
 * FAILED    — o POST /invitees falhou (slot perdido, 429, erro). NÃO deletar:
 *             é a trilha de auditoria de "tentou agendar e não conseguiu".
 */
export const AppointmentStatusEnum = z.enum([
  'PENDING',
  'SCHEDULED',
  'CANCELED',
  'COMPLETED',
  'NO_SHOW',
  'FAILED',
]);
export type AppointmentStatus = z.infer<typeof AppointmentStatusEnum>;

export const AppointmentSourceEnum = z.enum(['EVA_APP', 'CALENDLY_WEBHOOK']);
export type AppointmentSource = z.infer<typeof AppointmentSourceEnum>;


export const AppointmentCalendlyDataSchema = z.object({
  eventUri: z.string().nullable().default(null),
  inviteeUri: z.string().nullable().default(null),
  eventTypeUri: z.string(),
  /** URLs de gestão devolvidas pelo Calendly; seguem válidas mesmo se o parceiro desconectar. */
  cancelUrl: z.string().nullable().default(null),
  rescheduleUrl: z.string().nullable().default(null),
  /**
   * Link da chamada. Para Google Meet/Zoom normalmente NÃO vem preenchido no
   * invitee.created — é criado de forma assíncrona e aparece depois. Enquanto
   * for null, a tela mostra "o link será liberado em breve".
   */
  joinUrl: z.string().nullable().default(null),
  locationKind: CalendlyLocationKindEnum,
  /** Endereço, telefone ou instrução livre, conforme o kind. */
  locationText: z.string().nullable().default(null),
});

export const AppointmentCancellationSchema = z.object({
  canceledAt: z.coerce.date(),
  canceledBy: z.enum(['invitee', 'host', 'eva']),
  reason: z.string().nullable().default(null),
  /**
   * O Calendly dispara invitee.canceled + invitee.created num reagendamento.
   * Quando true, NÃO notifique aqui: o par created traz o horário novo e a
   * notificação correta é uma só, de "reagendado".
   */
  rescheduled: z.boolean().default(false),
});

export const AppointmentSchema = z.object({
  /** id do documento == id (gravado com .doc(id).set(), não com add()). */
  id: z.string(),
  partnerId: z.string(),
  /** null quando o agendamento veio do Calendly sem corresponder a uma usuária. */
  userId: z.string().nullable().default(null),
  medicalProviderId: z.string(),
  mappingId: z.string(),

  status: AppointmentStatusEnum,
  source: AppointmentSourceEnum,

  calendly: AppointmentCalendlyDataSchema,

  startTime: z.coerce.date(),
  endTime: z.coerce.date(),
  timezone: z.string().default('America/Sao_Paulo'),

  inviteeSnapshot: z.object({
    name: z.string(),
    email: z.string().email(),
    timezone: z.string(),
  }),
  questionsAndAnswers: z.array(z.object({
    question: z.string(),
    answer: z.string(),
    position: z.number().int().min(0),
  })).default([]),

  /**
   * Congelado no momento do agendamento. A listagem "minhas consultas" não
   * pode fazer N+1 em medicalProviders, e o card de preparo mostrado deve ser
   * o que valia no dia — não o que o parceiro editou depois.
   */
  providerSnapshot: z.object({
    prefix: z.string().nullable().default(null),
    name: z.string(),
    portraitUrl: z.string().nullable().default(null),
    occupation: z.string().nullable().default(null),
    specialtyName: z.string().nullable().default(null),
    eventTypeName: z.string(),
    durationMinutes: z.number().int().positive(),
  }),
  preparationCardsSnapshot: z.array(PreparationCardSchema).default([]),

  cancellation: AppointmentCancellationSchema.nullable().default(null),
  rescheduledFromAppointmentId: z.string().nullable().default(null),
  rescheduledToAppointmentId: z.string().nullable().default(null),

  /**
   * Espelho do menor `dueAt` PENDING. Existe para o dispatcher fazer query de
   * range num campo simples — o Firestore não consulta dentro de arrays de
   * objetos. null = nada pendente.
   */

  /** Enviada pelo app; impede que duplo-toque com resposta lenta crie dois invitees. */
  idempotencyKey: z.string().nullable().default(null),
  lastWebhookEventId: z.string().nullable().default(null),
  lastWebhookAt: z.coerce.date().nullable().default(null),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});
export type Appointment = z.infer<typeof AppointmentSchema>;

/** Estados terminais: nada mais acontece com o agendamento depois de entrar neles. */
const TERMINAL_STATUSES: readonly AppointmentStatus[] = [
  'CANCELED',
  'COMPLETED',
  'NO_SHOW',
  'FAILED',
] as const;

/**
 * Valida uma transição de status.
 *
 * O ponto principal é bloquear regressões a partir de estados terminais: sem
 * isso, um `invitee.created` reentregue pelo Calendly depois de um
 * cancelamento ressuscitaria o agendamento e a usuária veria uma consulta que
 * não existe mais.
 */
export function canTransitionAppointment(
  from: AppointmentStatus,
  to: AppointmentStatus
): boolean {
  if (from === to) return false;
  if (TERMINAL_STATUSES.includes(from)) return false;
  if (from === 'PENDING') {
    return to === 'SCHEDULED' || to === 'FAILED' || to === 'CANCELED';
  }
  if (from === 'SCHEDULED') {
    return to === 'CANCELED' || to === 'COMPLETED' || to === 'NO_SHOW';
  }
  return false;
}

